import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { formatDollars } from '@/util/money';
import { z, ZodType } from 'zod';
import { Column, TableSettings } from '@/components/pages/kiosk/monitor/StaffMonitor/components/TableSettings';

export function formatCost(cost: number): ReactNode {
  return formatDollars(cost);
}

export function usePersistentState<T>(
  key: string,
  zodType: ZodType<T>,
  defaultValue: T | undefined = undefined
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
  const [state, setState] = useState<T | undefined>(() => {
    const saved = localStorage.getItem(key);
    const parsed = zodType.safeParse(saved ? JSON.parse(saved) : undefined);
    return parsed.success ? parsed.data : undefined;
  });

  useEffect(() => {
    if (state === undefined) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, JSON.stringify(state));
  }, [state]);

  return [state === undefined ? defaultValue : state, setState];
}

export function useSettings(columns: Column[]): [TableSettings, Dispatch<SetStateAction<TableSettings>>] {
  const [columnsSelectionAndOrdering, setColumnsSelectionAndOrdering] = usePersistentState(
    'staffMonitorSettings',
    z.tuple([z.string(), z.boolean()]).array()
  );

  const sanitizedColumns = useMemo(() => {
    if (columnsSelectionAndOrdering === undefined) {
      return columns.map(column => ({ key: column.key, title: column.title, visible: true }));
    }

    const selected = columnsSelectionAndOrdering.flatMap(([key, visible]) => {
      const column = columns.find(c => c.key === key);

      return column ? [{ ...column, visible }] : [];
    });

    const missing = columns.filter(
      column => columnsSelectionAndOrdering.find(([key]) => key === column.key) === undefined
    );

    return [...selected, ...missing.map(column => ({ ...column, visible: true }))];
  }, [columns, columnsSelectionAndOrdering]);

  const onChange: ReturnType<typeof useState<TableSettings>>[1] = valueOrFn => {
    if (typeof valueOrFn === 'function') {
      const { columns: newColumns } = valueOrFn({ columns: sanitizedColumns });
      setColumnsSelectionAndOrdering(newColumns.map(column => [column.key, column.visible] as [string, boolean]));
    } else if (valueOrFn === undefined) {
      setColumnsSelectionAndOrdering(undefined);
    } else {
      setColumnsSelectionAndOrdering(
        valueOrFn.columns.map(column => [column.key, column.visible] as [string, boolean])
      );
    }
  };

  return [{ columns: sanitizedColumns }, onChange];
}
